import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface PlanoBackend {
  planoId: number;
  planoNome: string;
  planoDescricao: string;
  /** Preço POR USUÁRIO no ciclo — o total é `planoPreco × quantidade`. */
  planoPreco: number;
  planoCiclo: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUALLY' | 'ANNUALLY' | 'TRIAL';
  planoAtivo: boolean;
  planoLimiteConexao: number | null;
  /* Renomeados quando o gateway deixou de ser só a AbacatePay: o provedor passou
     a ser escolhido por plano, então os identificadores são genéricos e vêm
     acompanhados de `planoProvedorPagamento`, que diz a qual gateway pertencem. */
  planoProvedorPagamento: ProvedorPagamento;
  planoIntegradoGateway: boolean;
  planoIsTrial: boolean;
  gatewayProductId: string | null;
  gatewayStatus: string | null;
  planoCreatedat: string;
}

/* Espelha `Brik.Domain.Enums.ProvedorPagamento`. A API serializa o nome em
   caixa alta. */
export type ProvedorPagamento = 'ABACATEPAY' | 'ASAAS';

export const NOME_PROVEDOR: Record<ProvedorPagamento, string> = {
  ABACATEPAY: 'AbacatePay',
  ASAAS: 'Asaas',
};

/** Rótulo do gateway para exibição; tolera provedor novo que a API passe a
 *  devolver antes do front conhecer. */
export const rotuloProvedor = (p: string | null | undefined): string =>
  (p && NOME_PROVEDOR[p as ProvedorPagamento]) || p || 'Gateway';

export type MetodoAssinatura = 'CARD' | 'PIX' | 'CARD_INSTALLMENT' | 'TRIAL';

export type EstadoAssinatura = 'trial' | 'vencida' | 'ativa';

export interface ResumoAssinatura {
  estado: EstadoAssinatura;
  /** Dias que faltam para o fim do teste; `null` fora do trial. */
  diasRestantes: number | null;
}

/**
 * O estado da assinatura para a pílula da sidebar, **sem nenhuma chamada de
 * API**: `empresaEmTrial`, `assinaturaVencida` e `dataExpiracaoPlano` já vêm do
 * `Auth/Me` e moram no `localStorage`.
 *
 * O nome do plano NÃO está lá — por isso a pílula do estado "ativa" não o
 * mostra. Buscá-lo custaria um `BuscarAssinatura` no carregamento de toda
 * tela, que é o oposto do "custo zero" combinado.
 */
export const lerResumoAssinatura = (): ResumoAssinatura => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.assinaturaVencida) return { estado: 'vencida', diasRestantes: null };
    if (user?.empresaEmTrial) {
      const fim = user?.dataExpiracaoPlano ? new Date(user.dataExpiracaoPlano) : null;
      /* Arredonda para cima: no último dia ainda se lê "1 dia", não "0". */
      const dias = fim && !Number.isNaN(fim.getTime())
        ? Math.max(0, Math.ceil((fim.getTime() - Date.now()) / 86_400_000))
        : null;
      return { estado: 'trial', diasRestantes: dias };
    }
    return { estado: 'ativa', diasRestantes: null };
  } catch {
    return { estado: 'ativa', diasRestantes: null };
  }
};

/** Métodos que NÃO aceitam `TrocarPlano`: PIX e parcelado não têm "próximo
 *  ciclo" para migrar, e a API recusa os dois com mensagens distintas. */
export const aceitaTrocaDePlano = (metodo: string | null | undefined): boolean =>
  metodo === 'CARD';

/** Por que a troca está indisponível, para a tela explicar em vez de só
 *  desabilitar o botão. */
export const motivoSemTroca = (metodo: string | null | undefined): string | null => {
  if (metodo === 'PIX')
    return 'Sua assinatura é PIX. Para mudar de plano, aguarde o vencimento e contrate o novo plano.';
  if (metodo === 'CARD_INSTALLMENT')
    return 'Sua compra foi parcelada. Para mudar de plano, aguarde o vencimento e contrate o novo plano.';
  return null;
};

export interface AssinaturaBackend {
  assinaturaId: number;
  planoId: number;
  gatewaySubscriptionId: string;
  /* A assinatura guarda o provedor com que foi criada — e ele NÃO acompanha a
     troca de plano: `TrocarPlano` procura o produto do plano novo sob este
     provedor (`AssinaturaService.cs:322`). */
  assinaturaProvedorPagamento: ProvedorPagamento;
  /* Os cinco do enum da API (`StatusAssinatura`). O `string` no fim é
     deliberado: a base de homologação já devolveu `ACCEP`, que o enum não
     produz — a tela trata desconhecido em vez de imprimir o código cru. */
  assinaturaStatus: 'TRIAL' | 'PENDING' | 'PAID' | 'CANCELLED' | 'EXPIRED' | (string & {});
  assinaturaCiclo: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUALLY' | 'ANNUALLY' | 'Gratuito';
  /** Total do ciclo: `planoPreco × assinaturaQuantidadeUsuarios`. */
  assinaturaValor: number;
  /** Usuários contratados — é o limite de usuários ativos da empresa. */
  assinaturaQuantidadeUsuarios: number;
  /* `CARD_INSTALLMENT` = compra única parcelada (Asaas, ciclo anual). Não é
     recorrente: não renova e não aceita troca de plano. */
  assinaturaMetodo: MetodoAssinatura | string;
  assinaturaCheckoutUrl: string | null;
  assinaturaInicioEm: string | null;
  assinaturaExpiraEm: string | null;
  nomePlano: string;
  descricaoPlano: string | null;
}

/** `PATCH /TrocarPlano` e `/AlterarQuantidadeUsuarios`: vazio na AbacatePay,
 *  com checkout na Asaas. */
export interface TrocarPlanoResultado {
  checkoutUrl?: string | null;
}

export interface ApiResponse<T> {
  sucesso: boolean;
  mensagem: string;
  dados: T;
}

export const billingService = {
  // Planos disponíveis para contratação (User)
  buscarPlanosDisponiveis: async (): Promise<ApiResponse<PlanoBackend[]>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/PlanosDisponiveis`);
    if (!response.ok) throw new Error('Erro ao carregar planos disponíveis');
    return response.json();
  },

  // Planos (Admin only)
  buscarTodosPlanos: async (): Promise<ApiResponse<PlanoBackend[]>> => {
    const response = await apiFetch(`${API_URL}/api/plano/BuscarTodos`);
    if (!response.ok) throw new Error('Erro ao carregar planos');
    return response.json();
  },

  buscarPlanoPorId: async (id: number): Promise<ApiResponse<PlanoBackend>> => {
    const response = await apiFetch(`${API_URL}/api/plano/BuscarPorId/${id}`);
    if (!response.ok) throw new Error('Erro ao buscar plano');
    return response.json();
  },

  criarPlano: async (data: {
    nome: string;
    descricao: string;
    preco: number;
    ciclo: string;
    limiteConexao?: number | null;
    integradoGateway?: boolean;
    ehPlanoTeste?: boolean;
    provedorPagamento?: ProvedorPagamento;
  }): Promise<ApiResponse<PlanoBackend>> => {
    const response = await apiFetch(`${API_URL}/api/plano/Criar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao criar plano');
    }
    return response.json();
  },

  atualizarPlano: async (data: {
    planoId: number;
    nome: string;
    descricao: string;
    preco: number;
    ativo: boolean;
    limiteConexao?: number | null;
    /* Obrigatório na prática: `AtualizarPlanoRequest` tem default "ABACATEPAY",
       então não mandar converte um plano Asaas para AbacatePay em silêncio. */
    provedorPagamento?: ProvedorPagamento;
  }): Promise<ApiResponse<any>> => {
    const response = await apiFetch(`${API_URL}/api/plano/Atualizar`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao atualizar plano');
    }
    return response.json();
  },

  removerPlano: async (id: number): Promise<ApiResponse<any>> => {
    const response = await apiFetch(`${API_URL}/api/plano/Remover/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao remover plano');
    }
    return response.json();
  },

  // Assinaturas (User)
  buscarAssinaturaAtiva: async (): Promise<ApiResponse<AssinaturaBackend | null>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/BuscarAssinatura`);
    if (response.status === 400 || response.status === 404) {
      // Retorna a resposta mesmo que seja erro 400 controlada de "nenhuma assinatura encontrada"
      const data = await response.json().catch(() => null);
      if (data && data.sucesso === false) {
        return data;
      }
    }
    if (!response.ok) throw new Error('Erro ao carregar assinatura');
    return response.json();
  },

  criarCheckout: async (
    planoId: number,
    quantidadeUsuarios: number,
  ): Promise<ApiResponse<AssinaturaBackend>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/CriarCheckout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planoId, quantidadeUsuarios }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao iniciar a contratação do plano');
    }
    return response.json();
  },

  /* `cpfCnpj` é obrigatório no Asaas quando a empresa ainda não tem CNPJ: o
     cadastro não pede, e o Asaas recusa cobrança PIX de cliente sem documento.
     Omitido, a API usa o CNPJ da empresa. */
  criarCheckoutPix: async (
    planoId: number,
    quantidadeUsuarios: number,
    cpfCnpj?: string | null,
  ): Promise<ApiResponse<AssinaturaBackend>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/CriarCheckoutPix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planoId, quantidadeUsuarios, cpfCnpj: cpfCnpj || undefined }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao iniciar o checkout PIX');
    }
    return response.json();
  },

  /* Compra única parcelada no cartão — só existe para plano com provedor ASAAS
     e ciclo ANNUALLY (a AbacatePay não parcela). Não renova sozinha: expira no
     fim do ciclo e exige contratação nova, como o PIX avulso. */
  criarCheckoutParcelado: async (
    planoId: number,
    quantidadeUsuarios: number,
    maxParcelas = 12,
  ): Promise<ApiResponse<AssinaturaBackend>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/CriarCheckoutParcelado`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planoId, quantidadeUsuarios, maxParcelas }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao iniciar o checkout parcelado');
    }
    return response.json();
  },

  /** O parcelado só é oferecido quando o plano comporta — as duas travas estão
   *  no `AssinaturaService` e a tela não deve mostrar opção que vai falhar. */
  aceitaParcelamento: (plano: PlanoBackend | undefined | null): boolean =>
    !!plano && plano.planoProvedorPagamento === 'ASAAS' && plano.planoCiclo === 'ANNUALLY',

  /* A resposta mudou com o multi-gateway. Na AbacatePay a troca é aplicada no
     próximo ciclo e `dados` vem nulo. Na Asaas a assinatura atual é CANCELADA e
     um checkout novo é gerado: `dados.checkoutUrl` precisa ser aberto, senão o
     cliente fica sem cobrança ativa e sem saber. */
  /* `quantidadeUsuarios` omitido mantém a quantidade atual da assinatura. */
  trocarPlano: async (
    novoPlanoId: number,
    quantidadeUsuarios?: number,
  ): Promise<ApiResponse<TrocarPlanoResultado | null>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/TrocarPlano`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ novoPlanoId, quantidadeUsuarios }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao trocar plano');
    }
    return response.json();
  },

  /* Mesmo contrato de resposta de `trocarPlano`: na Asaas a assinatura é
     cancelada e `dados.checkoutUrl` precisa ser aberto. */
  alterarQuantidadeUsuarios: async (
    quantidadeUsuarios: number,
  ): Promise<ApiResponse<TrocarPlanoResultado | null>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/AlterarQuantidadeUsuarios`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantidadeUsuarios }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.mensagem || 'Erro ao alterar a quantidade de usuários');
    }
    return response.json();
  },

  cancelarAssinatura: async (): Promise<ApiResponse<any>> => {
    const response = await apiFetch(`${API_URL}/api/assinatura/Cancelar`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Erro ao cancelar assinatura');
    return response.json();
  },
};
